import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FileText, Download, Calendar, Tag, ExternalLink, Plus, Trash2, Save, Megaphone, Copy, Check,
  Mail, Eye, Printer, Send, Loader2, X, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('mosaic_session_token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const CATEGORY_COLORS = {
  'sell-sheet': 'bg-blue-100 text-blue-800 border-blue-300',
  'procurement': 'bg-indigo-100 text-indigo-800 border-indigo-300',
  'one-pager': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'case-study': 'bg-amber-100 text-amber-800 border-amber-300',
  'presentation': 'bg-purple-100 text-purple-800 border-purple-300',
  'flyer': 'bg-pink-100 text-pink-800 border-pink-300',
  'email-template': 'bg-cyan-100 text-cyan-800 border-cyan-300',
  'agreement': 'bg-amber-100 text-amber-800 border-amber-300',
  'other': 'bg-gray-100 text-gray-700 border-gray-300',
};

const GRADE_BANDS = ['', 'K-5', '6-8', '9-12', 'K-12'];

/* ─── Welcome Email Template Card ─── */
const WelcomeEmailCard = () => {
  const [showSend, setShowSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ school_name: '', contact_name: '', contact_email: '', school_address: '', order_number: '', grade_band: '' });

  const previewUrl = () => {
    const params = new URLSearchParams({
      school_name: form.school_name || '[School Name]',
      contact_name: form.contact_name || '[Contact Name]',
      school_address: form.school_address || '[School Address]',
      order_number: form.order_number || '[Order #]',
      grade_band: form.grade_band || '',
    });
    return `${API}/collateral/welcome-email/preview?${params.toString()}`;
  };

  const fetchHtml = async () => {
    const token = getToken();
    const res = await fetch(previewUrl(), { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Failed to load');
    return res.text();
  };

  const handlePreview = async () => {
    try {
      const html = await fetchHtml();
      const win = window.open('', '_blank', 'width=700,height=900');
      win.document.write(html);
      win.document.close();
    } catch { toast.error('Failed to load preview'); }
  };

  const handlePrint = async () => {
    try {
      const html = await fetchHtml();
      const win = window.open('', '_blank', 'width=700,height=900');
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 500);
    } catch { toast.error('Failed to load for printing'); }
  };

  const handleDownload = async () => {
    try {
      const html = await fetchHtml();
      const blob = new Blob([html], { type: 'text/html' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `MOSAIC_Welcome_Email${form.school_name ? '_' + form.school_name.replace(/\s+/g, '_') : ''}.html`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download'); }
  };

  const handleCopyHtml = async () => {
    try {
      const html = await fetchHtml();
      await navigator.clipboard.writeText(html);
      setCopied(true);
      toast.success('HTML copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Failed to copy HTML'); }
  };

  const handleSend = async () => {
    if (!form.school_name.trim() || !form.contact_name.trim() || !form.contact_email.trim()) {
      toast.error('School Name, Contact Name, and Contact Email are required');
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const res = await axios.post(`${API}/collateral/welcome-email/send`, form, authHeader());
      setSendResult(res.data);
      if (res.data.email_delivered) {
        toast.success(`Welcome email sent to ${form.contact_email}`);
      } else {
        toast.warning('Credentials generated but email delivery failed — check SendGrid');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white border-2 border-[#002FA7]/20 p-5" data-testid="welcome-email-template-card">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-[#002FA7] rounded flex items-center justify-center">
            <Mail size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-800">Welcome to MOSAIC Email Template</h3>
            <p className="text-[10px] text-gray-500">Digital order onboarding — admin credentials, Quick Start, license terms</p>
          </div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded border ${CATEGORY_COLORS['email-template']}`}>
          email-template
        </span>
      </div>

      {/* Customize fields */}
      <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
        <p className="text-[10px] font-bold text-gray-600 mb-2 uppercase tracking-wide">Customize Preview</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] text-gray-500">School Name *</label>
            <Input
              value={form.school_name}
              onChange={e => setForm({ ...form, school_name: e.target.value })}
              placeholder="PS 124 Yung Wing"
              className="h-7 text-xs"
              data-testid="welcome-school-name"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">School Address</label>
            <Input
              value={form.school_address}
              onChange={e => setForm({ ...form, school_address: e.target.value })}
              placeholder="40 Division St, New York, NY 10002"
              className="h-7 text-xs"
              data-testid="welcome-school-address"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">Order Number</label>
            <Input
              value={form.order_number}
              onChange={e => setForm({ ...form, order_number: e.target.value })}
              placeholder="MOS-Q-20260411-A1B2C3"
              className="h-7 text-xs"
              data-testid="welcome-order-number"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">Contact Name *</label>
            <Input
              value={form.contact_name}
              onChange={e => setForm({ ...form, contact_name: e.target.value })}
              placeholder="Maria Torres"
              className="h-7 text-xs"
              data-testid="welcome-contact-name"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">Contact Email *</label>
            <Input
              value={form.contact_email}
              onChange={e => setForm({ ...form, contact_email: e.target.value })}
              placeholder="mtorres@schools.nyc.gov"
              className="h-7 text-xs"
              data-testid="welcome-contact-email"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">Grade Band</label>
            <select
              value={form.grade_band}
              onChange={e => setForm({ ...form, grade_band: e.target.value })}
              className="w-full h-7 text-xs border border-gray-200 rounded px-2 bg-white"
              data-testid="welcome-grade-band"
            >
              {GRADE_BANDS.map(g => (
                <option key={g} value={g}>{g || 'None'}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={handlePreview}
          className="text-xs h-8 border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5 gap-1"
          style={{ borderRadius: 0 }}
          data-testid="welcome-preview-btn"
        >
          <Eye size={13} /> Preview
        </Button>
        <Button
          variant="outline"
          onClick={handlePrint}
          className="text-xs h-8 border-gray-300 text-gray-700 hover:bg-gray-50 gap-1"
          style={{ borderRadius: 0 }}
          data-testid="welcome-print-btn"
        >
          <Printer size={13} /> Print
        </Button>
        <Button
          variant="outline"
          onClick={handleDownload}
          className="text-xs h-8 border-gray-300 text-gray-700 hover:bg-gray-50 gap-1"
          style={{ borderRadius: 0 }}
          data-testid="welcome-download-btn"
        >
          <Download size={13} /> Download HTML
        </Button>
        <Button
          variant="outline"
          onClick={handleCopyHtml}
          className="text-xs h-8 border-gray-300 text-gray-700 hover:bg-gray-50 gap-1"
          style={{ borderRadius: 0 }}
          data-testid="welcome-copy-html-btn"
        >
          {copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
          {copied ? 'Copied!' : 'Copy as HTML'}
        </Button>
        <Button
          onClick={() => setShowSend(!showSend)}
          className="text-xs h-8 bg-[#002FA7] hover:bg-[#001f6e] text-white gap-1"
          style={{ borderRadius: 0 }}
          data-testid="welcome-send-toggle-btn"
        >
          <Send size={13} /> Email to Contact
        </Button>
      </div>

      {/* Send form */}
      {showSend && (
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded p-3" data-testid="welcome-send-form">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-blue-800">Send Welcome Email</p>
            <button onClick={() => { setShowSend(false); setSendResult(null); }} className="text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          </div>
          {!form.school_name.trim() || !form.contact_name.trim() || !form.contact_email.trim() ? (
            <p className="text-[10px] text-amber-600 mb-2">Fill in School Name, Contact Name, and Contact Email above to send.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-[10px] text-blue-700">
                This will generate real admin credentials and send the Welcome Email to <strong>{form.contact_email}</strong> for <strong>{form.school_name}</strong>.
              </p>
              <Button
                onClick={handleSend}
                disabled={sending}
                className="text-xs h-8 bg-[#002FA7] hover:bg-[#001f6e] text-white gap-1"
                style={{ borderRadius: 0 }}
                data-testid="welcome-send-btn"
              >
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                Send Now
              </Button>
            </div>
          )}

          {/* Result */}
          {sendResult && (
            <div className="mt-2 bg-white border border-blue-200 rounded p-2 space-y-1 text-[10px]" data-testid="welcome-send-result">
              {!sendResult.email_delivered && (
                <div className="bg-amber-50 border border-amber-300 rounded px-2 py-1 text-amber-700 font-bold mb-1">
                  Email delivery failed — credentials generated below. Verify SendGrid API key.
                </div>
              )}
              <p className="font-bold text-gray-700">Generated Admin Credentials:</p>
              <div className="font-mono bg-gray-50 px-2 py-1 rounded">
                <span className="text-gray-500">Email:</span> <span className="font-bold">{sendResult.admin_email}</span>
              </div>
              <div className="font-mono bg-amber-50 px-2 py-1 rounded border border-amber-200">
                <span className="text-gray-500">Password:</span> <span className="font-bold text-amber-700">{sendResult.admin_password}</span>
              </div>
              <p className="text-gray-500">
                {sendResult.email_delivered
                  ? `Sent to ${sendResult.sent_to}`
                  : 'Email not delivered — share credentials manually.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Beta Participation Agreement Card ─── */
const BetaAgreementCard = () => {
  const [downloading, setDownloading] = useState(false);
  const [form, setForm] = useState({ school_name: '', contact_name: '', grade_bands: 'K-12' });

  const handlePreviewDownload = async () => {
    setDownloading(true);
    try {
      // Use a placeholder signup to generate a preview PDF via backend
      const params = new URLSearchParams({
        school_name: form.school_name || '[School Name]',
        contact_name: form.contact_name || '[Contact Name]',
        grade_bands: form.grade_bands || 'K-12',
      });
      const token = getToken();
      const res = await fetch(`${API}/beta-signups/preview-agreement?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to generate');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MOSAIC_Beta_Agreement_Preview.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Beta Agreement PDF downloaded');
    } catch {
      toast.error('Failed to download agreement');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-white border-2 border-amber-300/50 p-5" data-testid="beta-agreement-card">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-amber-500 rounded flex items-center justify-center">
            <Shield size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-800">Beta Participation Agreement</h3>
            <p className="text-[10px] text-gray-500">10-section legal agreement — auto-attached to beta approval emails</p>
          </div>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded border bg-amber-100 text-amber-800 border-amber-300">
          agreement
        </span>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
        <p className="text-[10px] font-bold text-gray-600 mb-2 uppercase tracking-wide">Preview with Custom Info</p>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] text-gray-500">School Name</label>
            <Input
              value={form.school_name}
              onChange={e => setForm({ ...form, school_name: e.target.value })}
              placeholder="PS 124 Yung Wing"
              className="h-8 text-xs"
              data-testid="beta-agreement-school"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">Contact Name</label>
            <Input
              value={form.contact_name}
              onChange={e => setForm({ ...form, contact_name: e.target.value })}
              placeholder="Jane Smith"
              className="h-8 text-xs"
              data-testid="beta-agreement-contact"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">Grade Bands</label>
            <Input
              value={form.grade_bands}
              onChange={e => setForm({ ...form, grade_bands: e.target.value })}
              placeholder="K-2, 3-5"
              className="h-8 text-xs"
              data-testid="beta-agreement-grades"
            />
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-3 leading-relaxed">
        Covers: access period (through Sept 30, 2026), acceptable use, FERPA compliance, intellectual property, feedback expectations, termination (Provider can terminate without cause), liability, and NJ governing law. Schools accept by replying "I AGREE" + returning signed PDF.
      </div>

      <div className="flex gap-2">
        <Button
          onClick={handlePreviewDownload}
          disabled={downloading}
          className="flex-1 bg-amber-500 hover:bg-amber-600 text-white text-xs h-8"
          style={{ borderRadius: 0 }}
          data-testid="beta-agreement-download-btn"
        >
          {downloading ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />}
          Download Preview PDF
        </Button>
      </div>

      <p className="text-[10px] text-gray-400 mt-2">
        This PDF is automatically generated and attached when you approve a beta application.
      </p>
    </div>
  );
};

/* ─── Main Sales Collateral Tab ─── */
const SalesCollateralTab = () => {
  const [collateral, setCollateral] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [newItem, setNewItem] = useState({
    title: '',
    category: 'sell-sheet',
    description: '',
    file_url: '',
    version: '1.0',
  });

  const fetchCollateral = async () => {
    try {
      const res = await axios.get(`${API}/collateral`);
      setCollateral(res.data.items || []);
    } catch {
      toast.error('Failed to load collateral');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCollateral(); }, []);

  const handleAdd = async () => {
    if (!newItem.title.trim() || !newItem.file_url.trim()) {
      toast.error('Title and file URL are required');
      return;
    }
    try {
      await axios.post(`${API}/collateral`, newItem);
      toast.success('Collateral added');
      setShowAdd(false);
      setNewItem({ title: '', category: 'sell-sheet', description: '', file_url: '', version: '1.0' });
      fetchCollateral();
    } catch {
      toast.error('Failed to add collateral');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this collateral item?')) return;
    try {
      await axios.delete(`${API}/collateral/${id}`);
      toast.success('Deleted');
      fetchCollateral();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleSeedCatalog = async () => {
    try {
      const res = await axios.post(`${API}/collateral/seed-catalog`);
      const ins = res.data?.inserted?.length || 0;
      const upd = res.data?.updated?.length || 0;
      toast.success(`Catalog synced — ${ins} new, ${upd} updated`);
      fetchCollateral();
    } catch {
      toast.error('Failed to seed catalog');
    }
  };

  const copyId = (collateralId) => {
    navigator.clipboard.writeText(collateralId);
    setCopiedId(collateralId);
    toast.success('ID copied');
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading collateral...</div>;

  return (
    <div className="space-y-6" data-testid="sales-collateral-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Sales & Marketing Collateral
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {collateral.length + 2} item{collateral.length + 2 !== 1 ? 's' : ''} — each with a unique identifier for tracking
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleSeedCatalog}
            variant="outline"
            className="text-xs h-9 px-3 border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5"
            style={{ borderRadius: 0 }}
            data-testid="seed-catalog-btn"
            title="Sync first-party catalog items (Principal Flyer, etc.) into the collateral list"
          >
            Sync First-Party Catalog
          </Button>
          <Button
            onClick={() => setShowAdd(!showAdd)}
            className="bg-[#002FA7] hover:bg-[#001f6e] text-white text-xs h-9 px-4"
            style={{ borderRadius: 0 }}
            data-testid="add-collateral-btn"
          >
            <Plus size={14} className="mr-1" /> Add Collateral
          </Button>
        </div>
      </div>

      {/* Add Form */}
      {showAdd && (
        <div className="bg-white border-2 border-[#002FA7]/20 p-5 space-y-4" data-testid="add-collateral-form">
          <h3 className="text-sm font-bold text-gray-800">New Collateral Item</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600">Title *</label>
              <Input
                value={newItem.title}
                onChange={e => setNewItem({ ...newItem, title: e.target.value })}
                placeholder="MOSAIC Fall 2026 Sell Sheet"
                data-testid="collateral-title-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Category</label>
              <select
                className="w-full h-10 border border-gray-300 rounded px-3 text-sm"
                value={newItem.category}
                onChange={e => setNewItem({ ...newItem, category: e.target.value })}
                data-testid="collateral-category-select"
              >
                <option value="sell-sheet">Sell Sheet</option>
                <option value="procurement">Procurement</option>
                <option value="one-pager">One-Pager</option>
                <option value="case-study">Case Study</option>
                <option value="presentation">Presentation</option>
                <option value="flyer">Flyer</option>
                <option value="email-template">Email Template</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-600">Description</label>
              <Input
                value={newItem.description}
                onChange={e => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="2-page color PDF with product catalog and comparison table"
                data-testid="collateral-desc-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">File URL / Path *</label>
              <Input
                value={newItem.file_url}
                onChange={e => setNewItem({ ...newItem, file_url: e.target.value })}
                placeholder="/api/blm-downloads/MOSAIC_Sell_Sheet_Fall_2026.pdf"
                data-testid="collateral-url-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Version</label>
              <Input
                value={newItem.version}
                onChange={e => setNewItem({ ...newItem, version: e.target.value })}
                placeholder="1.0"
                data-testid="collateral-version-input"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={handleAdd} className="bg-[#002FA7] hover:bg-[#001f6e] text-white text-xs h-8 px-4" style={{ borderRadius: 0 }} data-testid="save-collateral-btn">
              <Save size={14} className="mr-1" /> Save
            </Button>
            <Button variant="outline" onClick={() => setShowAdd(false)} className="text-xs h-8 px-4" style={{ borderRadius: 0 }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Welcome Email Template — Pinned */}
      <WelcomeEmailCard />

      {/* Beta Participation Agreement — Pinned */}
      <BetaAgreementCard />

      {/* Collateral Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {collateral.map((item) => (
          <div key={item.collateral_id} className="bg-white border border-gray-200 p-4 hover:border-[#002FA7]/30 transition-colors" data-testid={`collateral-card-${item.collateral_id}`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-[#002FA7] flex-shrink-0" />
                <h3 className="text-sm font-bold text-gray-800 leading-tight">{item.title}</h3>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded border ${CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other}`}>
                {item.category}
              </span>
            </div>

            {/* v6.32.223 — First-party badge */}
            {item.first_party && (
              <div className="mb-2">
                <span className="text-[9px] font-bold px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 uppercase tracking-wider" data-testid={`first-party-${item.collateral_id}`}>
                  First-party · ships with app
                </span>
              </div>
            )}

            {/* Description */}
            {item.description && (
              <p className="text-xs text-gray-500 mb-3 leading-relaxed">{item.description}</p>
            )}

            {/* Unique ID */}
            <div className="flex items-center gap-1.5 mb-3 bg-gray-50 px-2 py-1.5 rounded">
              <Tag size={11} className="text-gray-400" />
              <code className="text-[10px] font-mono text-[#002FA7] font-semibold">{item.collateral_id}</code>
              <button
                onClick={() => copyId(item.collateral_id)}
                className="ml-auto text-gray-400 hover:text-[#002FA7] transition-colors"
                title="Copy ID"
                data-testid={`copy-id-${item.collateral_id}`}
              >
                {copiedId === item.collateral_id ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
              </button>
            </div>

            {/* Meta */}
            <div className="flex items-center gap-3 text-[10px] text-gray-400 mb-3">
              <span className="flex items-center gap-1">
                <Calendar size={10} /> {new Date(item.created_at).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                v{item.version}
              </span>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <a
                href={item.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
                data-testid={`download-${item.collateral_id}`}
              >
                <Button variant="outline" className="w-full text-xs h-8 border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5" style={{ borderRadius: 0 }}>
                  <Download size={13} className="mr-1" /> {item.press_url ? 'Open (office)' : 'Download'}
                </Button>
              </a>
              {/* v6.32.223 — Press-mode link for first-party flyers */}
              {item.press_url && (
                <a
                  href={item.press_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`press-${item.collateral_id}`}
                >
                  <Button variant="outline" className="text-xs h-8 px-2 border-amber-300 text-amber-700 hover:bg-amber-50" style={{ borderRadius: 0 }} title="Open in print-shop mode (8.75×11.25 with crop marks + bleed)">
                    Press
                  </Button>
                </a>
              )}
              {/* First-party items are protected from accidental deletion */}
              {!item.first_party && (
                <Button
                  variant="outline"
                  onClick={() => handleDelete(item.collateral_id)}
                  className="text-xs h-8 px-2 border-red-200 text-red-500 hover:bg-red-50"
                  style={{ borderRadius: 0 }}
                  data-testid={`delete-${item.collateral_id}`}
                >
                  <Trash2 size={13} />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {collateral.length === 0 && !showAdd && (
        <div className="text-center py-12 text-gray-400">
          <Megaphone size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No collateral yet. Click "Add Collateral" to get started.</p>
        </div>
      )}
    </div>
  );
};

export default SalesCollateralTab;
